/**
 * API origin for cross-subdomain production (e.g. https://api.emailperfect.io).
 * Leave unset in dev so requests stay same-origin `/api` and use the Vite proxy.
 *
 * Must read `import.meta.env.VITE_API_ORIGIN` directly — esbuild `define` in
 * `scripts/build-landing.mjs` only replaces that exact expression (not via `.env`).
 */
import { rewriteDevPublicUrl } from "./devPublicUrl";

function apiOrigin(): string {
  const raw = import.meta.env?.VITE_API_ORIGIN as string | undefined;
  const fromEnv = raw?.trim().replace(/\/+$/, "") ?? "";
  if (fromEnv) {
    return fromEnv;
  }
  // Landing bundle fallback when VITE_API_ORIGIN is not inlined at build time.
  if (typeof window !== "undefined") {
    const host = window.location.hostname;
    if (host === "emailperfect.io" || host === "www.emailperfect.io") {
      return "https://api.emailperfect.io";
    }
  }
  return "";
}

/** Prefix for REST paths, e.g. `/api` or `https://api.example.com/api`. */
export const API_BASE = apiOrigin() ? `${apiOrigin()}/api` : "/api";

export { apiOrigin };

/**
 * In dev (Vite proxy), presign returns absolute `http://localhost:8787/api/...` URLs.
 * Route uploads and stored asset URLs through same-origin `/api/...` and `/assets/...`
 * so cookies, CORS, and sandboxed preview iframes match the SPA origin.
 */
export function toSameOriginApiUrl(absoluteOrRelativeUrl: string): string {
  if (apiOrigin()) {
    return absoluteOrRelativeUrl;
  }
  if (typeof window === "undefined") {
    return absoluteOrRelativeUrl;
  }
  return rewriteDevPublicUrl(absoluteOrRelativeUrl, window.location.origin);
}

/** Fetch with cookies for cross-origin API (same-site subdomains). */
export function apiFetch(input: string | URL, init?: RequestInit): Promise<Response> {
  return fetch(input, {
    ...init,
    credentials: init?.credentials ?? "include",
  });
}
